import { MinusCircleFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Checkbox,
  Col,
  Form,
  Input,
  List,
  Popconfirm,
  Popover,
  Row,
  Tooltip,
} from 'antd';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Accent,
  CurrencyDisplay,
  CustomButton,
  DocumentViewModal,
  NumericFormat,
} from '@/lib/components';
import { Invoice, PaymentInvoiceItem } from '@/lib/types';
import {
  currencyFormatter,
  dateTimeFormatter,
  percentageFormatter,
} from '@/lib/utils/formatters';
import {
  getDifferences,
  getPaymentInfo,
} from '../_helpers/paymentInvoiceHelper';
import { CreditRow } from './CreditRow';
import { PartialPaymentRow } from './PartialPaymentRow';

const toFiniteNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

interface PaymentInvoiceProps {
  paymentInvoice: PaymentInvoiceItem;
  invoiceIndex: number;
  isDisabled: boolean;
  isChecked: boolean;
  onCheck: (invoiceId: number, checked: boolean) => void;
  onRemoveInvoice: (invoiceIndex: number, invoice: Invoice) => Promise<void>;
  onRemoveCreditInvoice: (
    invoiceIndex: number,
    invoice: Invoice,
  ) => Promise<void>;
  renameableRelationIds: string[];
}

export const PaymentInvoice = memo(
  ({
    paymentInvoice,
    invoiceIndex,
    isDisabled,
    isChecked,
    onCheck,
    onRemoveInvoice,
    onRemoveCreditInvoice,
    renameableRelationIds,
  }: PaymentInvoiceProps) => {
    const { t } = useTranslation();
    const form = Form.useFormInstance();
    const invoice = paymentInvoice.invoice;
    const paymentSetting = paymentInvoice.paymentSetting;
    const partialPayments =
      (Form.useWatch(['invoices', invoiceIndex, 'partialPayments'], form) as
        | PaymentInvoiceItem['partialPayments']
        | undefined) ?? [];
    const nettingInvoices = paymentInvoice.nettingInvoices ?? [];
    const isExcluded = Boolean(paymentInvoice.isExcluded);
    const toPayValue = Form.useWatch(['invoices', invoiceIndex, 'toPay'], form);

    const path = useMemo(() => [invoiceIndex], [invoiceIndex]);

    const amount = toFiniteNumber(invoice?.amount);
    const toPay = toFiniteNumber(toPayValue, amount);
    const isRenameable = invoice
      ? renameableRelationIds.includes(invoice.supplier.relationId)
      : false;

    const paymentInfo = useMemo(
      () => (invoice ? getPaymentInfo(invoice, paymentSetting) : undefined),
      [invoice, paymentSetting],
    );

    const differences = useMemo(
      () =>
        invoice ? getDifferences(invoice, paymentSetting, partialPayments) : [],
      [invoice, partialPayments, paymentSetting],
    );

    const remainingValue = useMemo(() => {
      return partialPayments.reduce(
        (accumulator, partialPayment) =>
          accumulator - toFiniteNumber(partialPayment?.value),
        toPay,
      );
    }, [partialPayments, toPay]);

    if (!invoice || !paymentInfo) {
      return null;
    }

    const syncSinglePartialPayment = (nextToPay: number) => {
      if (partialPayments.length !== 1) {
        return;
      }

      form.setFieldValue(
        ['invoices', invoiceIndex, 'partialPayments', 0, 'value'],
        nextToPay,
      );
    };

    const handleDiscountChange = (nextDiscountValue: string) => {
      const nextDiscount = toFiniteNumber(nextDiscountValue);
      const nextToPay = amount - amount * (nextDiscount / 100);

      form.setFieldValue(['invoices', invoiceIndex, 'discount'], nextDiscount);
      form.setFieldValue(['invoices', invoiceIndex, 'toPay'], nextToPay);
      syncSinglePartialPayment(nextToPay);
    };

    const handleToPayChange = (nextToPayValue: string) => {
      const nextToPay = toFiniteNumber(nextToPayValue, amount);
      const nextDiscount = amount > 0 ? 100 - (nextToPay / amount) * 100 : 0;

      form.setFieldValue(['invoices', invoiceIndex, 'toPay'], nextToPay);
      form.setFieldValue(
        ['invoices', invoiceIndex, 'discount'],
        nextToPay > amount ? 0 : nextDiscount,
      );
      syncSinglePartialPayment(nextToPay);
    };

    const handleRemoveInvoiceClick = async () => {
      const previousInvoices =
        (form.getFieldValue('invoices') as PaymentInvoiceItem[] | undefined) ??
        [];

      form.setFieldValue(
        ['invoices'],
        previousInvoices.filter((_, index) => index !== invoiceIndex),
      );

      try {
        await onRemoveInvoice(invoiceIndex, invoice);
      } catch {
        form.setFieldValue(['invoices'], previousInvoices);
      }
    };

    const handleRemoveCreditInvoiceClick = async (invoice: Invoice) => {
      const previousInvoices =
        (form.getFieldValue('invoices') as PaymentInvoiceItem[] | undefined) ??
        [];
      const nextInvoices = [...previousInvoices];
      const nextPaymentInvoice = nextInvoices[invoiceIndex];

      if (!nextPaymentInvoice) {
        return;
      }

      nextInvoices[invoiceIndex] = {
        ...nextPaymentInvoice,
        nettingInvoices: nextPaymentInvoice.nettingInvoices.filter(
          (nettingInvoice) => nettingInvoice.invoiceId !== invoice.invoiceId,
        ),
      };

      form.setFieldValue(['invoices'], nextInvoices);

      try {
        await onRemoveCreditInvoice(invoiceIndex, invoice);
      } catch {
        form.setFieldValue(['invoices'], previousInvoices);
      }
    };

    return (
      <Row gutter={10}>
        <Col span={13} className="font-scale">
          <Row gutter={10}>
            <Col span={1}>
              <Form.Item noStyle shouldUpdate>
                <Checkbox
                  checked={isChecked}
                  onChange={(e) => onCheck(invoice.invoiceId, e.target.checked)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              {dateTimeFormatter.toDateOnly(invoice.invoiceDate)}
            </Col>
            <Col span={5}>{invoice.invoiceNumber}</Col>
            <Col span={4}>
              <Form.Item name={[...path, 'receiverName']} noStyle>
                <Input className="dashed" disabled={!isRenameable} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <CurrencyDisplay amount={amount} />
            </Col>
            <Col span={3}>
              <Form.Item
                name={[...path, 'discount']}
                normalize={percentageFormatter().parse}
              >
                <NumericFormat
                  className="font-scale ant-input dashed-number"
                  allowNegative={false}
                  isAllowed={(value) =>
                    !value.floatValue ||
                    (value.floatValue >= 0 && value.floatValue <= 100)
                  }
                  onValueChange={({ value }) => handleDiscountChange(value)}
                  decimalSeparator=","
                  fixedDecimalScale={false}
                  decimalScale={2}
                  suffix=" %"
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                name={[...path, 'toPay']}
                normalize={currencyFormatter().parse}
                rules={[
                  {
                    type: 'number',
                    max: amount,
                    message: t('payment.toPayMessage'),
                  },
                ]}
              >
                <NumericFormat
                  className="dashed-number font-scale"
                  step={1}
                  allowNegative={false}
                  fixedDecimalScale={true}
                  decimalSeparator=","
                  decimalScale={2}
                  thousandSeparator="."
                  prefix="€ "
                  onValueChange={({ value }) => handleToPayChange(value)}
                />
              </Form.Item>
            </Col>
          </Row>
          {nettingInvoices.length > 0 && (
            <Row gutter={10}>
              <Col span={1}></Col>
              <Accent color="tertiary">{t('invoiceTypes.CreditNote')}</Accent>
            </Row>
          )}
          {nettingInvoices.map((invoice) => (
            <CreditRow
              key={invoice.invoiceId}
              invoice={invoice}
              remove={handleRemoveCreditInvoiceClick}
            />
          ))}
        </Col>

        <Col span={9}>
          {isExcluded ? (
            <Badge count="Uitgesteld"></Badge>
          ) : (
            <Form.List name={[...path, 'partialPayments']}>
              {(fields, { add, remove }) => {
                return (
                  <>
                    {fields.map((field, index) => (
                      <PartialPaymentRow
                        key={field.key}
                        field={field}
                        showRemove={index > 0}
                        remove={remove}
                        disabled={isDisabled}
                        paymentSetting={paymentSetting}
                        term={paymentInfo.paymentTerm}
                      />
                    ))}
                    <Row gutter={10}>
                      <Col style={{ textAlign: 'left' }} xs={8}>
                        <CustomButton
                          color="secondary"
                          ghost
                          disabled={isDisabled}
                          shape="round"
                          toolTipKey="payment.action.addPayment"
                          onClick={() => {
                            add({
                              ...paymentInfo,
                              value: Math.max(remainingValue, 0),
                            });
                          }}
                          icon={<FontAwesomeIcon icon="plus" />}
                        >
                          {t('payment.addPayment')}
                        </CustomButton>
                      </Col>
                      <Col
                        style={{ textAlign: 'right' }}
                        xs={14}
                        className="font-scale"
                      >
                        <span>{t('payment.header.toPay')}: </span>
                        <CurrencyDisplay amount={toPay} />
                        <br />
                        <span>{t('payment.remainingValue')}: </span>
                        <span
                          style={{
                            color: remainingValue < 0 ? 'red' : 'black',
                          }}
                        >
                          <CurrencyDisplay amount={remainingValue} />
                        </span>
                      </Col>
                      <Col span={2}>
                        {toPay > amount && (
                          <Tooltip title={t('payment.toPayWarning')}>
                            <CustomButton
                              size="small"
                              color="primary"
                              shape="circle"
                              toolTipKey="payment.action.deleteInvoice"
                              icon={<FontAwesomeIcon icon="exclamation" />}
                            />
                          </Tooltip>
                        )}
                      </Col>
                    </Row>
                    <Row justify="end">
                      <Col flex={1}></Col>
                      <Col>
                        {remainingValue < 0 && (
                          <span style={{ color: 'red' }}>
                            {t('payment.overvalued')}
                          </span>
                        )}
                      </Col>
                      <Col span={2}></Col>
                    </Row>
                  </>
                );
              }}
            </Form.List>
          )}
        </Col>
        <Col span={2} style={{ textAlign: 'right' }}>
          <DocumentViewModal
            id={invoice.sourceIdentifier}
            idType="processfile"
          />
          <Popconfirm
            title="Factuur niet meer betalen"
            cancelText={t('general.actions.cancel')}
            onConfirm={handleRemoveInvoiceClick}
            disabled={isDisabled}
            okButtonProps={{
              shape: 'round',
            }}
            cancelButtonProps={{
              shape: 'round',
            }}
          >
            <Badge
              count={<MinusCircleFilled />}
              offset={[-3, 3]}
              style={{ color: '#f5222d', fontSize: '18px' }}
              size="default"
            >
              <CustomButton
                style={{ marginBottom: '3px' }}
                ghost
                disabled={isDisabled}
                shape="circle"
                toolTipKey="payment.action.deleteInvoice"
                color="secondary"
                icon={<FontAwesomeIcon icon={['far', 'credit-card']} />}
              />
            </Badge>
          </Popconfirm>
          {differences.length > 0 && (
            <Popover
              title={t('payment.differenceDetected')}
              content={
                <List
                  dataSource={differences}
                  split={false}
                  bordered={false}
                  size="small"
                  renderItem={(differenceKey) => (
                    <List.Item key={differenceKey}>
                      {t(differenceKey)}
                    </List.Item>
                  )}
                />
              }
            >
              <CustomButton
                color="primary"
                shape="circle"
                toolTipKey="payment.action.showDifferences"
                icon={<FontAwesomeIcon icon="exclamation" />}
              />
            </Popover>
          )}
        </Col>
      </Row>
    );
  },
);

PaymentInvoice.displayName = 'PaymentInvoice';
